import React, { useState, useEffect, ChangeEvent } from 'react';
import { Box, Button, Stack, Text } from '@tonic-ui/react';
import { Modal } from '../../../components/modal';
import { useTranslation } from 'react-i18next';

import ProfilingModalTile from './profiling-modal-tile';
import ConfirmationModal from './confirmation-modal';
import { profilingChoices, services } from './constants';
import { sendEventTrack } from '../../../utils/helpers';

export interface ServiceMatch {
  id: string;
  title: string;
  message: string;
}

interface ProfilingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProfilingModal = ({ isOpen, onClose }: ProfilingModalProps): JSX.Element => {
  const { t } = useTranslation();
  const initialCheckState = new Array(6).fill(false);
  const [checkedState, setCheckedState] = useState<boolean[]>(initialCheckState);

  const MODAL_VIEWS = {
    PROFILING: 'profilingView',
    CONFIRMATION: 'confirmationView',
  };

  const [activeView, setActiveView] = useState({
    view: MODAL_VIEWS.PROFILING,
  });

  const numChoices = checkedState.reduce((total, value) => (value ? total + 1 : total), 0);

  useEffect(() => {
    if (isOpen) {
      // Trigger custom track event for Pendo
      sendEventTrack('C1_PROFILE_MODAL_DISPLAYED', {
        Topic: 'SentryProfilingModal',
        Event: 'Sentry Profile Modal Displayed',
      });
    }
  }, [isOpen]);

  const handleChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const updatedCheckedState = [...checkedState];
    updatedCheckedState[index] = event?.target?.checked;
    setCheckedState(updatedCheckedState);
  };

  // Get user's choices
  const getUserChoices = () => {
    return profilingChoices.filter((_, index) => checkedState[index]);
  };
  const userChoices = getUserChoices();

  const userChoiceIds: string[] = userChoices.map((choice) => {
    return choice.id;
  });

  const userResponse = userChoiceIds.join(',');

  // Profile validation
  const handleMatchedServices = () => {
    return services.filter((service, index) => checkedState[index]);
  };
  const matchedSecurityServices = handleMatchedServices();

  const proceedToNextView = () => {
    setActiveView((prevState) => ({
      ...prevState,
      view: MODAL_VIEWS.CONFIRMATION,
    }));

    // Trigger custom track event for Pendo
    // Step 1 - user answers up to 3 questions
    sendEventTrack('C1_PROFILE_QNS1', {
      Topic: 'SentryProfilingModal',
      Event: 'Sentry Profiling Questionnaire',
      Indicator: 'User selected questions',
      UserResponse: userResponse,
    });
  };

  const closeModal = () => {
    // Reset to profiling view when closing.
    setActiveView({
      view: MODAL_VIEWS.PROFILING,
    });
    setCheckedState(initialCheckState);
    onClose();
  };

  return (
    <Modal
      id="onboarding-profiling"
      isOpen={isOpen}
      onClose={closeModal}
      header={
        <>
          {activeView.view === MODAL_VIEWS.PROFILING ? (
            <Stack spacing="6x">
              <Text size="xl">{t('onboarding.profilingView.title')}</Text>
              <Stack spacing="1x">
                <Text size="sm">{t('onboarding.profilingView.message')}</Text>
                <Text size="sm">{t('onboarding.profilingView.selectTxt')}</Text>
              </Stack>
            </Stack>
          ) : (
            activeView.view === MODAL_VIEWS.CONFIRMATION && (
              <Stack spacing="6x">
                <Text size="xl">{t('onboarding.confirmationView.title')}</Text>
                <Text size="sm">{t('onboarding.confirmationView.message')}</Text>
              </Stack>
            )
          )}
        </>
      }
      body={
        <>
          {activeView.view === MODAL_VIEWS.PROFILING ? (
            <Stack spacing="3x" marginTop="20px">
              {profilingChoices.map(({ id, choice }, index) => (
                <Box key={index}>
                  <ProfilingModalTile
                    choiceId={id}
                    choice={t(choice)}
                    checked={checkedState[index] as boolean}
                    disabled={!checkedState[index] && numChoices === 3}
                    onChange={(e) => handleChange(index, e)}
                  />
                </Box>
              ))}
            </Stack>
          ) : (
            activeView.view === MODAL_VIEWS.CONFIRMATION && (
              <ConfirmationModal matchedServices={matchedSecurityServices} />
            )
          )}
        </>
      }
      footer={
        <>
          {activeView.view === MODAL_VIEWS.PROFILING && (
            <Button
              variant="primary"
              onClick={() => proceedToNextView()}
              disabled={numChoices === 0}
              data-id="onboarding-profiling-modal-next-button"
              data-telemetry-id="onboarding-profiling-modal-next-button"
            >
              {t('next')}
            </Button>
          )}
        </>
      }
    />
  );
};

ProfilingModal.displayName = 'ProfilingModal';

export default ProfilingModal;
